import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { fromZonedTime, toZonedTime } from "date-fns-tz";
import { Fire, Phone, Timer, Trophy, TrendUp } from "@phosphor-icons/react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { RepCard } from "../components/shared/RepCard";
import { api } from "../lib/api";

const navItems = [{ to: "/admin/command-center", label: "Command Center", icon: <TrendUp size={18} weight="duotone" />, testId: "nav-admin-command-center" }];

const DEAL_STAGES = ["Pipeline", "Submitted", "Underwriting", "Approved", "Funded", "Declined"];
const STAGE_COLORS = {
  Pipeline: "border-igc-gold/20 text-igc-muted",
  Submitted: "border-igc-gold/30 text-igc-goldText/80",
  Underwriting: "border-amber-600/40 text-amber-600",
  Approved: "border-igc-gold/40 text-igc-goldText",
  Funded: "border-igc-success/50 text-igc-success",
  Declined: "border-igc-danger/40 text-igc-danger",
};

function getNextWednesday5pmET() {
  const nowET = toZonedTime(new Date(), "America/New_York");
  const target = new Date(nowET);
  target.setHours(12, 0, 0, 0);
  const dayDiff = (3 - target.getDay() + 7) % 7;
  target.setDate(target.getDate() + dayDiff);
  if (dayDiff === 0 && nowET.getTime() > target.getTime()) {
    target.setDate(target.getDate() + 7);
  }
  return fromZonedTime(target, "America/New_York");
}

function useCutoffCountdown() {
  const [remaining, setRemaining] = useState("");
  useEffect(() => {
    const tick = () => {
      const diffMs = getNextWednesday5pmET().getTime() - Date.now();
      const d = Math.floor(diffMs / 86400000);
      const h = Math.floor((diffMs % 86400000) / 3600000);
      const m = Math.floor((diffMs % 3600000) / 60000);
      const s = Math.floor((diffMs % 60000) / 1000);
      setRemaining(`${d}d ${h}h ${m}m ${s}s`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return remaining;
}

export default function AdminCommandCenterPage() {
  const navigate = useNavigate();
  const [reps, setReps] = useState([]);
  const [center, setCenter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const cutoff = useCutoffCountdown();

  useEffect(() => {
    Promise.all([api.get("/admin/reps/lead-activity"), api.get("/admin/command-center")])
      .then(([repsRes, centerRes]) => {
        setReps(repsRes.data);
        setCenter(centerRes.data);
      })
      .catch(() => toast.error("Couldn't load the Command Center. Please refresh."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      Promise.all([api.get("/admin/reps/lead-activity"), api.get("/admin/command-center")])
        .then(([repsRes, centerRes]) => {
          setReps(repsRes.data);
          setCenter(centerRes.data);
        })
        .catch(() => {});
    }, 15000);
    return () => clearInterval(interval);
  }, []);

  const dealTotals = useMemo(() => {
    if (!center) return { count: 0, value: 0 };
    return Object.values(center.deal_pipeline).reduce(
      (acc, s) => ({ count: acc.count + s.count, value: acc.value + s.value }),
      { count: 0, value: 0 }
    );
  }, [center]);

  const filteredReps = useMemo(() => {
    if (!search.trim()) return reps;
    const q = search.trim().toLowerCase();
    return reps.filter((r) => r.display_name?.toLowerCase().includes(q) || r.user_id?.toLowerCase().includes(q));
  }, [reps, search]);

  return (
    <DashboardLayout navItems={navItems} moduleName="Command Center" moduleBackPath="/admin">
      <div data-testid="admin-command-center-page">
        <div className="mb-8">
          <h1 className="font-serif text-3xl text-igc-cream tracking-tight mb-1" title="Every rep, every deal, and every dollar in the business — refreshed live every 15 seconds.">IGC Command Center</h1>
          <p className="text-igc-muted text-sm">Every rep, every deal, every dollar — live, in one place.</p>
        </div>

        {loading && <p className="text-igc-muted text-sm" data-testid="command-center-loading">Loading the operation...</p>}

        {!loading && center && (
          <div className="space-y-10">
            {/* Rep Activity Board */}
            <section data-testid="command-center-rep-board">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-igc-goldText text-xs uppercase tracking-widest font-medium">Rep Activity Board</h2>
                <input
                  data-testid="command-center-rep-search-input"
                  placeholder="Search by name or rep code..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  title="Search narrows the rep cards below in real time as you type — matches name or rep code."
                  className="bg-igc-surface border border-igc-gold/20 text-igc-cream text-sm rounded px-3 py-1.5 w-56 focus:outline-none focus:border-igc-gold/50"
                />
              </div>
              {reps.length === 0 ? (
                <p className="text-igc-muted text-sm">No reps onboarded yet.</p>
              ) : filteredReps.length === 0 ? (
                <p className="text-igc-muted text-sm" data-testid="command-center-rep-search-empty">No reps match "{search}".</p>
              ) : (
                <div className="flex gap-4 overflow-x-auto pb-2" data-hscroll>
                  {filteredReps.map((r) => (
                    <RepCard key={r.id} rep={r} onOpen={(rep) => navigate(`/admin/rep-activity/${rep.id}`)} onUpload={() => navigate("/admin/rep-activity")} />
                  ))}
                </div>
              )}
            </section>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Commission Tracker */}
              <section className="bg-igc-surface border border-igc-gold/15 p-5" data-testid="command-center-commission-tracker">
                <h2 className="text-igc-goldText text-xs uppercase tracking-widest font-medium mb-4 flex items-center gap-2"><Timer size={14} weight="duotone" /> Commission Tracker</h2>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div data-testid="command-center-commission-pending" title="Commission that's been earned by reps but not yet approved/paid out.">
                    <p className="font-serif text-2xl text-amber-600">${center.commission.total_pending.toLocaleString()}</p>
                    <p className="text-igc-muted text-[11px] uppercase tracking-wide">Pending</p>
                  </div>
                  <div data-testid="command-center-commission-paid" title="Commission that has already been paid out to reps.">
                    <p className="font-serif text-2xl text-igc-success">${center.commission.total_paid.toLocaleString()}</p>
                    <p className="text-igc-muted text-[11px] uppercase tracking-wide">Paid</p>
                  </div>
                </div>
                <div className="border-t border-igc-gold/10 pt-3">
                  <p className="text-igc-muted text-[11px] uppercase tracking-wide mb-1" title="Commissions must be finalized by this weekly deadline to make it into that week's payout run.">Next Payout Cutoff (Wed 12PM ET)</p>
                  <p className="font-serif text-lg text-igc-cream" data-testid="command-center-cutoff-timer">{cutoff}</p>
                </div>
              </section>

              {/* Deal Pipeline */}
              <section className="bg-igc-surface border border-igc-gold/15 p-5 lg:col-span-2" data-testid="command-center-deal-pipeline">
                <h2 className="text-igc-goldText text-xs uppercase tracking-widest font-medium mb-4 flex items-center gap-2"><TrendUp size={14} weight="duotone" /> Deal Pipeline ({dealTotals.count} active)</h2>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                  {DEAL_STAGES.map((stage) => (
                    <div key={stage} className={`border p-3 text-center ${STAGE_COLORS[stage]}`} data-testid={`command-center-stage-${stage.toLowerCase()}`} title={`Number and total dollar value of deals currently sitting at the ${stage} stage across every rep.`}>
                      <p className="font-serif text-xl">{center.deal_pipeline[stage]?.count ?? 0}</p>
                      <p className="text-[10px] uppercase tracking-wide mt-1">{stage}</p>
                      <p className="text-[10px] mt-1 opacity-70">${(center.deal_pipeline[stage]?.value ?? 0).toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              </section>
            </div>

            {/* Hot Lead Feed */}
            <section data-testid="command-center-hot-lead-feed">
              <h2 className="text-igc-goldText text-xs uppercase tracking-widest font-medium mb-3 flex items-center gap-2"><Fire size={14} weight="fill" className="text-igc-danger" /> Hot Lead Feed</h2>
              {center.hot_leads.length === 0 ? (
                <p className="text-igc-muted text-sm">No high-intent leads flagged right now.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {center.hot_leads.map((l) => (
                    <div key={l.id} className="bg-igc-surface border border-igc-danger/30 p-4 relative overflow-hidden" data-testid={`command-center-hot-lead-${l.id}`} title="This lead has a high intent score — likely ready to close soon, worth calling right away.">
                      <div className="absolute top-0 right-0 w-2 h-2 bg-igc-danger rounded-full m-3 animate-pulse" />
                      <p className="text-igc-cream text-sm font-medium pr-4">{l.name}</p>
                      <p className="text-igc-muted text-xs mb-2 capitalize">{l.source} — intent {l.intent_score}</p>
                      {l.phone && (
                        <a href={`tel:${l.phone}`} data-testid={`command-center-hot-lead-call-${l.id}`} title="Dials this lead's phone number directly from your device." className="inline-flex items-center gap-1.5 bg-igc-gold hover:bg-igc-goldHover text-black text-xs px-3 py-1.5 rounded-full">
                          <Phone size={12} weight="fill" /> Call
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Leaderboard */}
            <section data-testid="command-center-leaderboard">
              <h2 className="text-igc-goldText text-xs uppercase tracking-widest font-medium mb-3 flex items-center gap-2"><Trophy size={14} weight="duotone" /> Leaderboard</h2>
              <div className="bg-igc-surface border border-igc-gold/15 p-5">
                {(!center.leaderboard || center.leaderboard.length === 0) ? (
                  <p className="text-igc-muted text-sm" data-testid="command-center-leaderboard-empty">No rep activity yet — rankings will populate as commissions are earned.</p>
                ) : (
                  <div className="space-y-2">
                    {center.leaderboard.map((row) => (
                      <div key={row.id} data-testid={`command-center-leaderboard-row-${row.id}`} className="flex items-center justify-between text-sm text-igc-cream border-b border-igc-gold/10 pb-2">
                        <span>#{row.rank} {row.display_name} <span className="text-igc-muted text-xs">({row.user_id})</span></span>
                        <span className="text-igc-goldText flex items-center gap-3">
                          <span className="text-igc-muted text-xs">{row.deals_submitted} deals</span>
                          ${row.commissions_earned.toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
